import { PROTOCOL_TEST_IDS } from '@/components/admin/protocoles/contracts';
import { FarewellBadge } from '@/components/admin/protocoles/timeline/farewell-badge';
import { stepCardName } from '@/components/admin/protocoles/timeline/step-card';
import type { ObjectiveLookup } from '@/lib/protocol-labels';
import { conditionShortLabel, objectiveLookupOf } from '@/lib/protocol-labels';
import type { ProtocolStepDraft } from '@matter_extractor/shared';
import { isTerminalAction } from '@matter_extractor/shared';
import { Icon, Tooltip } from '@pms-digital/design-system';
import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';

/**
 * The TREE of a protocol (refonte modulaire §7.1; maquette `treeColumn` /
 * `treeBranch`).
 *
 * §7.1, word for word: « le chemin normal en colonne, sous chaque étape ses
 * conditions de sortie puis son repli en branches, avant la suite ; une branche
 * redéroule sa cible entièrement, dépliée par défaut et repliable ; une étape
 * déjà sur le chemin est une feuille "retour à N", les sorties du protocole et
 * de l'expérience des feuilles. Cliquer une étape dans l'arbre la sélectionne. »
 *
 * ## In READING, always
 *
 * No insertion, no drag, no trash - the tree answers « où va cette étape », and
 * every gesture that changes the protocol is on the frise or in the step's own
 * panels. The one thing it does is select, which is a move of the eye and not
 * an edit.
 *
 * ## Why a target is UNROLLED rather than linked to
 *
 * A tree of links would ask the reader to hold the rest of the branch in their
 * head. Unrolled, « et ensuite ? » is answered where it is asked - and the
 * recursion terminates because a step already on the current path becomes a
 * leaf. That cut is what makes a loop finite: `s5 → s4 → s5` draws twice and
 * then says « retour à 5 · Hypothèse » rather than for ever.
 *
 * ## Folding is per BRANCH, and it lives here
 *
 * The fold key is the path that leads to the branch plus the condition it comes
 * from, so the same target unrolled under two different steps folds
 * independently - the only reading that matches what is on screen. It is view
 * state, like the zoom of the frise: nothing about it belongs to the draft.
 */

export interface ProtocolTreeProps {
  readonly steps: readonly ProtocolStepDraft[];
  readonly selectedStepId: string;
  readonly onSelect: (stepId: string) => void;
}

/** Everything a column needs that does not change as it walks. */
type TreeContext = {
  readonly steps: readonly ProtocolStepDraft[];
  readonly ranks: ReadonlyMap<string, number>;
  readonly objectiveAt: ObjectiveLookup;
  readonly selectedStepId: string;
  readonly onSelect: (stepId: string) => void;
  readonly folded: ReadonlySet<string>;
  readonly toggleFold: (key: string) => void;
};

const LABEL_PILL =
  'inline-flex flex-none items-center gap-1.5 rounded-sm bg-bleu-nuit-tint px-2 py-0.5 font-semibold text-bleu-nuit-deep text-caption';
/** The repli wears a border and no ground: it is not a condition (§7.2). */
const FALLBACK_PILL =
  'inline-flex flex-none items-center gap-1.5 rounded-sm border border-gray-300 px-2 py-0.5 font-semibold text-gray-700 text-caption';

/*
  The three ends a path can have. Written out rather than composed, because a
  `border-dashed` and a `border-solid` in one string are decided by the order
  Tailwind emits them and not by the order they are written - a class list is
  not a cascade.
*/
const LEAF_LOOP =
  'inline-flex items-center gap-1.5 rounded-sm border border-gray-400 border-dashed px-2.5 py-1 text-gray-700 text-small';
const LEAF_END =
  'inline-flex items-center gap-1.5 rounded-sm border border-gray-400 px-2.5 py-1 text-gray-700 text-small';
/** The end of a farewell step's path: the plain end, in the ink of its badge. */
const LEAF_FAREWELL =
  'inline-flex items-center gap-1.5 rounded-sm border border-bleu-nuit px-2.5 py-1 text-bleu-nuit-deep text-small';
const LEAF_CLOSED =
  'inline-flex items-center gap-1.5 rounded-sm border border-rouge-deep border-dashed px-2.5 py-1 text-rouge-deep text-small';

/** One step of the normal path: the number, the name, the attempts badge. */
function TreeNode({
  step,
  index,
  ctx,
}: {
  step: ProtocolStepDraft;
  index: number;
  ctx: TreeContext;
}): ReactNode {
  const selected = step.id === ctx.selectedStepId;
  return (
    <button
      type="button"
      data-testid={PROTOCOL_TEST_IDS.treeNode}
      data-step-id={step.id}
      aria-pressed={selected}
      onClick={() => ctx.onSelect(step.id)}
      className={`inline-flex cursor-pointer items-center gap-2 rounded-sm px-2.5 py-1 text-small ${
        selected
          ? 'border-2 border-bleu-nuit bg-bleu-nuit-tint'
          : 'border border-gray-300 bg-white hover:border-gray-500'
      }`}
    >
      <span className="text-caption text-gray-500 tabular-nums">{index + 1}</span>
      <b className="font-semibold">{stepCardName(step, index)}</b>
      {step.farewell ? <FarewellBadge /> : null}
      {/* A farewell step's rules are ignored (H76): no attempts to announce. */}
      {!step.farewell && step.rules.attempts > 1 ? (
        <Tooltip content={`jusqu’à ${step.rules.attempts} essais`}>
          <span
            aria-hidden="true"
            className="inline-flex h-4 items-center gap-0.5 rounded-full bg-bleu-ciel-tint px-1.5 font-semibold text-[10.5px] text-bleu-ciel-deep tabular-nums"
          >
            <Icon name="repeat" size={14} />
            {step.rules.attempts}
          </span>
        </Tooltip>
      ) : null}
    </button>
  );
}

/** A branch that ENDS: a loop back, a closed experience, a closed protocol. */
function TreeLeaf({
  label,
  fallback,
  children,
}: {
  label: string;
  fallback: boolean;
  children: ReactNode;
}): ReactNode {
  return (
    <div className="flex items-start gap-2">
      <span className={fallback ? FALLBACK_PILL : LABEL_PILL}>{label}</span>
      {children}
    </div>
  );
}

/** A branch that UNROLLS its target, with the chevron that folds it. */
function TreeBranch({
  label,
  fallback,
  target,
  path,
  foldKey,
  ctx,
}: {
  label: string;
  fallback: boolean;
  target: string;
  path: readonly string[];
  foldKey: string;
  ctx: TreeContext;
}): ReactNode {
  const folded = ctx.folded.has(foldKey);
  return (
    <div className="flex items-start gap-2">
      <span className={fallback ? FALLBACK_PILL : LABEL_PILL}>
        {label}
        <button
          type="button"
          data-testid={PROTOCOL_TEST_IDS.treeFold}
          aria-expanded={!folded}
          aria-label={folded ? 'Déplier la branche' : 'Replier la branche'}
          onClick={() => ctx.toggleFold(foldKey)}
          className="inline-grid cursor-pointer place-items-center text-gray-500"
        >
          <span className={`inline-flex transition-transform ${folded ? '-rotate-90' : ''}`}>
            <Icon name="expand_more" size={14} />
          </span>
        </button>
        {folded ? <span className="font-normal text-gray-500">repliée</span> : null}
      </span>
      {folded ? null : <TreeColumn startId={target} path={path} ctx={ctx} />}
    </div>
  );
}

/**
 * A branch, with the ONE thing `TreeBranch` cannot decide for itself: a target
 * already on the path is a leaf and not a descent (§7.1), and that is what
 * keeps a loop finite.
 */
function Branch({
  label,
  fallback,
  target,
  path,
  foldKey,
  ctx,
}: {
  label: string;
  fallback: boolean;
  target: string;
  path: readonly string[];
  foldKey: string;
  ctx: TreeContext;
}): ReactNode {
  if (path.includes(target)) {
    const rank = ctx.ranks.get(target) ?? 0;
    const step = ctx.steps[rank];
    return (
      <TreeLeaf label={label} fallback={fallback}>
        <span className={LEAF_LOOP}>
          <Icon name="reply" size={14} />
          {`retour à ${rank + 1} · ${step === undefined ? '' : stepCardName(step, rank)}`}
        </span>
      </TreeLeaf>
    );
  }
  return (
    <TreeBranch
      label={label}
      fallback={fallback}
      target={target}
      path={path}
      foldKey={foldKey}
      ctx={ctx}
    />
  );
}

/**
 * The normal path from one step on: each step, then its branches, then the next
 * step - and a leaf when the path loops or the protocol ends (§7.1).
 *
 * The walk carries its OWN copy of the path and hands every branch a snapshot:
 * two branches of the same step must not see each other's descent.
 */
function TreeColumn({
  startId,
  path,
  ctx,
}: {
  startId: string;
  path: readonly string[];
  ctx: TreeContext;
}): ReactNode {
  const rows: ReactNode[] = [];
  const walked = [...path];
  let current: string | null = startId;

  while (current !== null) {
    const index = ctx.ranks.get(current);
    const step = index === undefined ? undefined : ctx.steps[index];
    if (step === undefined || index === undefined) break;
    walked.push(step.id);
    rows.push(<TreeNode key={`node-${step.id}`} step={step} index={index} ctx={ctx} />);

    /*
      A farewell step ENDS its path (§7.1, issue #163): « son chemin s'arrête
      sur elle, même si des étapes la suivent ». Entering it is concluding
      (§8.3) - one goodbye, then the protocol closes - so nothing after it is
      reached by the order, and it has no branch of its own to draw: no
      condition, and a repli the engine never reads (H76).
    */
    if (step.farewell) {
      rows.push(
        <span
          key={`farewell-${step.id}`}
          data-testid={PROTOCOL_TEST_IDS.treeFarewellEnd}
          className={LEAF_FAREWELL}
        >
          <Icon name="flag" size={14} />
          fin du protocole · page suivante
        </span>,
      );
      break;
    }

    const branches: ReactNode[] = [];
    for (const condition of step.conditions) {
      /* At most one terminal action, and it comes last (§8.4) - so the first
         one found IS the end of the condition. */
      const terminal = condition.actions.find(isTerminalAction);
      if (terminal === undefined) continue;
      const label = conditionShortLabel(condition, ctx.objectiveAt);
      const key = `${walked.join('>')}|${condition.id}`;
      if (terminal.kind === 'go-to-step' && ctx.ranks.has(terminal.stepId)) {
        branches.push(
          <Branch
            key={key}
            label={label}
            fallback={false}
            target={terminal.stepId}
            path={[...walked]}
            foldKey={key}
            ctx={ctx}
          />,
        );
      } else if (terminal.kind === 'close-experience') {
        branches.push(
          <TreeLeaf key={key} label={label} fallback={false}>
            <span className={LEAF_CLOSED}>
              <Icon name="cancel" size={14} />
              expérience fermée
            </span>
          </TreeLeaf>,
        );
      } else if (terminal.kind === 'close-protocol') {
        branches.push(
          <TreeLeaf key={key} label={label} fallback={false}>
            <span className={LEAF_END}>
              <Icon name="flag" size={14} />
              fin du protocole
            </span>
          </TreeLeaf>,
        );
      }
    }

    const { fallback } = step.rules;
    if (fallback.kind === 'go-to-step' && ctx.ranks.has(fallback.stepId)) {
      const key = `${walked.join('>')}|repli`;
      branches.push(
        <Branch
          key={key}
          label="repli"
          fallback={true}
          target={fallback.stepId}
          path={[...walked]}
          foldKey={key}
          ctx={ctx}
        />,
      );
    }
    if (branches.length > 0) {
      rows.push(
        <div
          key={`branches-${step.id}`}
          className="ml-3 flex flex-col gap-1.5 border-gray-200 border-l-2 pl-6"
        >
          {branches}
        </div>,
      );
    }

    const next = ctx.steps[index + 1];
    if (next === undefined) {
      /* « Clôturer la dernière étape, c'est clôturer le protocole » (§8.3). */
      rows.push(
        <span key={`end-${step.id}`} className={LEAF_END}>
          <Icon name="flag" size={14} />
          fin du protocole · page suivante
        </span>,
      );
      current = null;
    } else if (walked.includes(next.id)) {
      rows.push(
        <span key={`loop-${step.id}`} className={LEAF_LOOP}>
          <Icon name="reply" size={14} />
          {`suite : retour à ${index + 2} · ${stepCardName(next, index + 1)}`}
        </span>,
      );
      current = null;
    } else {
      current = next.id;
    }
  }

  return <div className="flex flex-col items-start gap-2">{rows}</div>;
}

export function ProtocolTree({ steps, selectedStepId, onSelect }: ProtocolTreeProps) {
  const [folded, setFolded] = useState<ReadonlySet<string>>(() => new Set<string>());
  const ranks = useMemo(
    () => new Map(steps.map((step, index) => [step.id, index] as const)),
    [steps],
  );
  const objectiveAt = useMemo(() => objectiveLookupOf(steps), [steps]);

  const ctx: TreeContext = {
    steps,
    ranks,
    objectiveAt,
    selectedStepId,
    onSelect,
    folded,
    toggleFold: (key) =>
      setFolded((current) => {
        const next = new Set(current);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      }),
  };

  const first = steps[0];
  return (
    <div
      data-testid={PROTOCOL_TEST_IDS.tree}
      className="mt-2.5 overflow-x-auto rounded-md border border-gray-200 bg-white p-5 text-small"
    >
      {first === undefined ? (
        <p className="text-gray-600">Aucune étape à dérouler.</p>
      ) : (
        <TreeColumn startId={first.id} path={[]} ctx={ctx} />
      )}
      <ul className="mt-4 flex flex-wrap gap-4 border-gray-100 border-t pt-3 text-caption text-gray-600">
        <li className="inline-flex items-center gap-1.5">
          <Icon name="reply" size={14} />
          boucle : retour à une étape déjà sur ce chemin
        </li>
        <li className="inline-flex items-center gap-1.5">
          <Icon name="expand_more" size={14} />
          replie la branche
        </li>
        <li className="inline-flex items-center gap-1.5">
          <Icon name="repeat" size={14} />
          plusieurs essais
        </li>
        <li className="inline-flex items-center gap-1.5">
          <Icon name="flag" size={14} />
          fin du protocole
        </li>
        <li className="inline-flex items-center gap-1.5">
          <span
            aria-hidden="true"
            className="inline-flex h-4 items-center rounded-full bg-bleu-nuit px-1.5 font-semibold text-[10.5px] text-white tracking-wide"
          >
            adieu
          </span>
          étape d’adieu : son chemin s’arrête là
        </li>
        <li className="inline-flex items-center gap-1.5">
          <Icon name="cancel" size={14} />
          expérience fermée
        </li>
        <li className="text-gray-500">
          sans repli dessiné, une étape ferme l’expérience au-delà de ses essais
        </li>
      </ul>
    </div>
  );
}
